/* STYRKR FUEL TOOL — WIZARD + RESULT RENDERER
   Mounts on .ft-app, renders all screens, drives state machine.
   Reads from window.STYRKR_FUEL_DATA. */

(function (window, document) {
  'use strict';

  var DATA = window.STYRKR_FUEL_DATA;
  if (!DATA) { console.error('[fuel-tool] data layer not loaded'); return; }

  /* ─────────────────────────────────────────────
     STATE
  ───────────────────────────────────────────── */
  var state = {
    mode: null,                 // 'race' | 'training' | 'quick'
    sport: null,                // discipline key
    sessionType: null,
    eventName: '',
    eventDate: '',
    durationMin: 240,
    intensity: null,
    weightKg: 75,
    weightUnit: 'kg',
    caffeine: null,
    weighting: null,
    gutTier: null,
    refineOpen: false,
    sweatRateMlH: null,
    sodiumTier: 'average',
    ftpW: null,
    thresholdPaceMinKm: null,
    goal: null,
    dietary: 'standard',
    flavour: 'berry',
    firstName: '',
    email: '',
    consent: false,            // UK GDPR / PECR — explicit opt-in only, never pre-ticked
    currentScreen: 'welcome',
    plan: null
  };

  // Note: 'refine' is NOT a separate screen — it's a panel inside 'gut'. Don't add it here.
  var SCREENS = ['welcome', 'sport', 'session', 'duration', 'intensity', 'weight', 'caffeine', 'weighting', 'gut', 'gate', 'result'];

  /* ─────────────────────────────────────────────
     SESSION PERSISTENCE
     Saves user inputs to sessionStorage so the wizard survives back-button
     navigation within the same tab (e.g. leaving to read about the
     Performance Lab and returning). Clears on tab close — sessionStorage,
     not localStorage, by design: avoids stale data on shared devices and
     dodges the "you have a saved plan" confirmation UX entirely.

     What we save: inputs + currentScreen.
     What we don't: `email`/`consent` (privacy + double-tracking), `plan`
     (cheap to regenerate from inputs; saving risks staleness if DATA changes).
  ───────────────────────────────────────────── */
  var STORAGE_KEY = 'styrkr.fuelTool.v1';
  // Whitelist of state keys we persist. Anything not in here is ignored on
  // both save and restore. Bump the version in STORAGE_KEY if this list
  // changes in a breaking way.
  var PERSIST_KEYS = [
    'mode', 'sport', 'sessionType', 'eventName', 'eventDate',
    'durationMin', 'intensity', 'weightKg', 'weightUnit',
    'caffeine', 'weighting', 'gutTier', 'refineOpen',
    'sweatRateMlH', 'sodiumTier', 'ftpW', 'thresholdPaceMinKm',
    'goal', 'dietary', 'flavour', 'firstName',
    'currentScreen'
  ];

  function persistState() {
    try {
      if (typeof sessionStorage === 'undefined') return;
      var payload = {};
      for (var i = 0; i < PERSIST_KEYS.length; i++) {
        var k = PERSIST_KEYS[i];
        payload[k] = state[k];
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // Quota exceeded, blocked storage, or private mode on old Safari.
      // Silent — persistence is a nice-to-have, never a blocker.
    }
  }

  function restoreState() {
    try {
      if (typeof sessionStorage === 'undefined') return;
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var payload = JSON.parse(raw);
      if (!payload || typeof payload !== 'object') return;
      // Only merge whitelisted keys — guards against tampered storage.
      for (var i = 0; i < PERSIST_KEYS.length; i++) {
        var k = PERSIST_KEYS[i];
        if (Object.prototype.hasOwnProperty.call(payload, k) && payload[k] !== undefined) {
          state[k] = payload[k];
        }
      }
      // If the user was last on the result screen, the plan isn't persisted
      // (intentional — see comment above). Regenerate it from the restored
      // inputs so the screen has something to render. If generation fails
      // (e.g. partial inputs), fall back to the gut screen so they can fix it.
      if (state.currentScreen === 'result') {
        try {
          var DATA = window.STYRKR_FUEL_DATA;
          if (DATA && typeof DATA.generatePlan === 'function') {
            state.plan = DATA.generatePlan(buildPlanInput());
          }
          if (!state.plan) state.currentScreen = 'gut';
        } catch (err) {
          state.currentScreen = 'gut';
          state.plan = null;
        }
      }
    } catch (e) {
      // Corrupted payload — discard, start fresh.
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
    }
  }

  /* ─────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────── */
  function el(id) { return document.getElementById(id); }
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function fmt(n) { return Math.round(n).toLocaleString('en-GB'); }
  function money(n) { return '£' + n.toFixed(2); }

  function trackKlaviyo(eventName, props) {
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', eventName, props || {}]);
      }
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', eventName, props || {}]);
      }
    } catch (e) { /* swallow */ }
  }

  function identifyKlaviyo(profile) {
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', profile]);
      }
    } catch (e) { /* swallow */ }
  }

  /* ─────────────────────────────────────────────
     SCREEN RENDERERS
  ───────────────────────────────────────────── */
  function renderWelcome() {
    return '' +
      '<section class="ft-screen is-active" data-screen="welcome">' +
        '<div class="ft-hero">' +
          '<div>' +
            '<div class="ft-eyebrow">The Fuel Tool · v1.0</div>' +
            '<h1 class="ft-display" style="margin-top:16px">FUEL<br>SMARTER<span class="ft-display-dot" aria-hidden="true">.</span></h1>' +
          '</div>' +
          '<p class="ft-hero-sub">Build a science-backed, per-session fueling plan in under 3 minutes. Used by athletes from sprint-distance to 200-mile ultras.</p>' +
        '</div>' +
        '<div class="ft-mode-grid">' +
          modeCard('01', 'race',     'RACE PLAN',     'Got an event? Build a race-day timeline.', '~3 min', true) +
          modeCard('02', 'training', 'TRAINING WEEK', 'Plan fueling across 7 days of training.', '~3 min') +
          modeCard('03', 'quick',    'QUICK SESSION', 'Just tomorrow\u2019s ride or run.',         '~2 min') +
        '</div>' +
      '</section>';
  }
  function modeCard(num, mode, title, desc, time, recommended) {
    return '<button class="ft-mode-card' + (recommended ? ' is-recommended' : '') + '" data-mode="' + mode + '">' +
      '<div class="ft-mode-card-head">' +
        '<div class="ft-mode-num">' + num + '</div>' +
        (recommended ? '<div class="ft-mode-tag">RECOMMENDED</div>' : '') +
      '</div>' +
      '<div>' +
        '<div class="ft-mode-title">' + title + '</div>' +
        '<div class="ft-mode-desc">' + desc + '</div>' +
      '</div>' +
      '<div class="ft-mode-meta"><span>' + time + '</span><span class="ft-mode-arrow">\u2192</span></div>' +
    '</button>';
  }

  function renderSport() {
    var disciplines = DATA.DISCIPLINES;
    // Cluster by category so related disciplines sit together
    var clusters = [
      { tag: 'RUN',  items: [
        { k: 'road_run',  label: 'Road' },
        { k: 'trail_run', label: 'Trail' },
        { k: 'ultra',     label: 'Ultra' }
      ]},
      { tag: 'BIKE', items: [
        { k: 'road_cycle',  label: 'Road' },
        { k: 'offroad_cyc', label: 'Off-road / MTB' }
      ]},
      { tag: 'TRI',  items: [
        { k: 'triathlon', label: 'Multi-sport' }
      ]}
    ];
    function clusterHtml(c) {
      return '<div class="ft-sport-cluster" data-cluster="' + c.tag.toLowerCase() + '">' +
        '<div class="ft-sport-cluster-head">' + c.tag + '</div>' +
        '<div class="ft-sport-cluster-body">' +
          c.items.map(function (it) {
            return '<button class="ft-opt ft-opt--sport" data-q="sport" data-val="' + it.k + '">' +
              '<div class="ft-opt-num">' + c.tag + '</div>' +
              '<div class="ft-opt-label">' + it.label + '</div>' +
            '</button>';
          }).join('') +
        '</div>' +
      '</div>';
    }
    return '' +
      '<section class="ft-screen is-active" data-screen="sport">' +
        '<div class="ft-q">' +
          '<h2 class="ft-h1 ft-q-title">What\u2019s your sport?</h2>' +
          '<p class="ft-q-help">Pick the discipline that matches your session. Each tunes carb, fluid, and sodium targets differently.</p>' +
          '<div class="ft-sport-clusters">' +
            clusters.map(clusterHtml).join('') +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderSession() {
    var labelByMode = {
      race: { title: 'What\u2019s the event?', help: 'Add the name and date so we can wire up race-week reminders.' },
      training: { title: 'What kind of week?', help: 'We\u2019ll calibrate volume across 7 days.' },
      quick: { title: 'What\u2019s tomorrow\u2019s session?', help: 'One session, just enough plan to nail it.' }
    };
    var l = labelByMode[state.mode] || labelByMode.quick;
    var sessionOpts = state.mode === 'race'
      ? [
          { k: 'race_day',   v: 'Race day',           d: 'Full race-day fueling timeline + pre-load notes.' },
          { k: 'race_prep',  v: 'Race prep session',  d: 'Dial in fueling before the event.' }
        ]
      : state.mode === 'training'
        ? [
            { k: 'build',    v: 'Build week',     d: 'High volume, normal carb intake.' },
            { k: 'recovery', v: 'Recovery week',  d: 'Lower volume, easy-paced.' },
            { k: 'peak',     v: 'Peak / taper',   d: 'Sharp sessions, fully fueled.' }
          ]
        : [
            { k: 'long',    v: 'Long session',          d: 'Endurance pace, 2h+ typically.' },
            { k: 'quality', v: 'Quality / intervals',   d: 'Hard efforts, threshold or above.' },
            { k: 'easy',    v: 'Easy session',          d: 'Aerobic, conversational pace.' }
          ];

    return '' +
      '<section class="ft-screen is-active" data-screen="session">' +
        '<div class="ft-q">' +
          '<h2 class="ft-h1 ft-q-title">' + l.title + '</h2>' +
          '<p class="ft-q-help">' + l.help + '</p>' +
          '<div class="ft-options ft-options-' + sessionOpts.length + '">' +
            sessionOpts.map(function (o, i) {
              return '<button class="ft-opt" data-q="sessionType" data-val="' + o.k + '">' +
                '<div class="ft-opt-num">0' + (i + 1) + '</div>' +
                '<div class="ft-opt-label">' + o.v + '</div>' +
                '<div class="ft-opt-desc">' + o.d + '</div>' +
              '</button>';
            }).join('') +
          '</div>' +
          (state.mode === 'race' ? raceMetaForm() : '') +
        '</div>' +
        navButtons() +
      '</section>';
  }
  function raceMetaForm() {
    return '<div class="ft-race-meta">' +
      '<div class="ft-form-row">' +
        '<label for="ft-event-name">Event name</label>' +
        '<input id="ft-event-name" type="text" data-bind="eventName" placeholder="London Marathon" />' +
      '</div>' +
      '<div class="ft-form-row">' +
        '<label for="ft-event-date">Event date</label>' +
        '<input id="ft-event-date" type="date" data-bind="eventDate" />' +
      '</div>' +
    '</div>';
  }

  function renderDuration() {
    var hrs = Math.floor(state.durationMin / 60);
    var mins = state.durationMin % 60;
    return '' +
      '<section class="ft-screen is-active" data-screen="duration">' +
        '<div class="ft-q">' +          '<h2 class="ft-h1 ft-q-title">How long is the session?</h2>' +
          '<p class="ft-q-help">Slide to set duration. We\u2019ll plan fueling across the whole effort.</p>' +
          '<div class="ft-slider-wrap">' +
            '<div class="ft-slider-readout" id="ft-dur-readout">' + hrs + '<span style="font-size:0.5em;color:var(--ft-mute);font-weight:400">h</span> ' + (mins < 10 ? '0' + mins : mins) + '<span style="font-size:0.5em;color:var(--ft-mute);font-weight:400">m</span></div>' +
            '<div class="ft-slider-unit">total duration</div>' +
            '<input type="range" min="30" max="720" step="15" value="' + state.durationMin + '" class="ft-slider" id="ft-dur-slider" style="--ft-fill:' + (((state.durationMin - 30) / (720 - 30)) * 100) + '%"  />' +
            '<div class="ft-slider-marks">' +
              '<span>30m</span><span>3h</span><span>6h</span><span>9h</span><span>12h</span>' +
            '</div>' +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderIntensity() {
    var ints = DATA.INTENSITY;
    return '' +
      '<section class="ft-screen is-active" data-screen="intensity">' +
        '<div class="ft-q">' +          '<h2 class="ft-h1 ft-q-title">What intensity?</h2>' +
          '<p class="ft-q-help">No FTP or threshold pace required at this layer \u2014 you can refine later.</p>' +
          '<div class="ft-options ft-options-4">' +
            Object.keys(ints).map(function (k) {
              return '<button class="ft-opt" data-q="intensity" data-val="' + k + '">' +
                '<div class="ft-opt-label">' + ints[k].label + '</div>' +
                '<div class="ft-opt-stat">~' + ints[k].effortPct + '% threshold</div>' +
              '</button>';
            }).join('') +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderWeight() {
    return '' +
      '<section class="ft-screen is-active" data-screen="weight">' +
        '<div class="ft-q">' +
          '<h2 class="ft-h1 ft-q-title">Body weight?</h2>' +
          '<p class="ft-q-help">Used to scale carb, fluid and sodium targets to your physiology.</p>' +
          '<div class="ft-num-input">' +
            '<button class="ft-num-step" data-step="-1" aria-label="Decrease weight">−</button>' +
            '<input type="number" id="ft-weight-input" inputmode="numeric" pattern="[0-9]*" value="' + state.weightKg + '" min="40" max="140" step="1" aria-label="Body weight" />' +
            '<span class="ft-num-unit-toggle" role="group" aria-label="Weight unit">' +
              '<button class="' + (state.weightUnit === 'kg' ? 'is-active' : '') + '" data-unit="kg" aria-pressed="' + (state.weightUnit === 'kg') + '">KG</button>' +
              '<button class="' + (state.weightUnit === 'lb' ? 'is-active' : '') + '" data-unit="lb" aria-pressed="' + (state.weightUnit === 'lb') + '">LB</button>' +
            '</span>' +
            '<button class="ft-num-step" data-step="1" aria-label="Increase weight">+</button>' +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderCaffeine() {
    var opts = [
      { k: 'yes',  l: 'Yes \u2014 full caf', d: 'Caffeinated GEL30 + MIX90 across the session' },
      { k: 'half', l: 'Half and half',      d: 'Alternating Caf and non-Caf gels' },
      { k: 'no',   l: 'No caf',             d: 'GEL50 only, MIX90 non-caf only' }
    ];
    return '' +
      '<section class="ft-screen is-active" data-screen="caffeine">' +
        '<div class="ft-q">' +          '<h2 class="ft-h1 ft-q-title">Caffeine preference?</h2>' +
          '<p class="ft-q-help">ISSN suggests 3\u20136 mg/kg for performance. We\u2019ll cap dose accordingly.</p>' +
          '<div class="ft-options ft-options-3">' +
            opts.map(function (o) {
              return '<button class="ft-opt" data-q="caffeine" data-val="' + o.k + '">' +
                '<div class="ft-opt-label">' + o.l + '</div>' +
                '<div class="ft-opt-desc">' + o.d + '</div>' +
              '</button>';
            }).join('') +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderWeighting() {
    var opts = [
      { k: 'gels',     l: 'Gels',     d: 'Compact, fast · GEL50-led, low fluid logistics' },
      { k: 'drinks',   l: 'Drinks',   d: 'Gut-friendly · MIX-led, easier on stomach' },
      { k: 'food',     l: 'Food',     d: 'Solid + satisfying · BAR50-led, trail / ultra friendly' },
      { k: 'balanced', l: 'Balanced', d: 'Orchestrated · all four products, optimised mix' }
    ];
    return '' +
      '<section class="ft-screen is-active" data-screen="weighting">' +
        '<div class="ft-q">' +
          '<h2 class="ft-h1 ft-q-title">How should the plan be weighted?</h2>' +
          '<p class="ft-q-help">Different athletes prefer different fuel formats. Pick what your stomach handles best.</p>' +
          '<div class="ft-options ft-options-4">' +
            opts.map(function (o, i) {
              return '<button class="ft-opt" data-q="weighting" data-val="' + o.k + '">' +
                '<div class="ft-opt-num">0' + (i + 1) + '</div>' +
                '<div class="ft-opt-label">' + o.l + '</div>' +
                '<div class="ft-opt-desc">' + o.d + '</div>' +
              '</button>';
            }).join('') +
          '</div>' +
        '</div>' +
        navButtons() +
      '</section>';
  }

  function renderGut() {
    var tiers = DATA.GUT_TIERS;
    return '' +
      '<section class="ft-screen is-active" data-screen="gut">' +
        '<div class="ft-q">' +
          '<h2 class="ft-h1 ft-q-title">How trained is your gut?</h2>' +
          '<p class="ft-q-help">High-rate carb intake is a trainable adaptation. Pick your honest current state.</p>' +
          '<div class="ft-options ft-options-3 ft-options--gut">' +
            Object.keys(tiers).map(function (k) {
              return '<button class="ft-opt ft-opt--gut" data-q="gutTier" data-val="' + k + '">' +
                '<div class="ft-opt-stat">' + tiers[k].target + ' g/h</div>' +
                '<div class="ft-opt-label">' + tiers[k].label + '</div>' +
              '</button>';
            }).join('') +
          '</div>' +
          '<div class="ft-refine-zone">' +
            '<button class="ft-refine-toggle" data-action="toggle-refine">' +
              '<span class="ft-refine-toggle-text">' +
                '<span class="ft-refine-toggle-label">+ Refine my plan</span>' +
                '<span class="ft-refine-toggle-sub">sweat rate · FTP · goal · dietary</span>' +
              '</span>' +
            '</button>' +
            '<div class="ft-refine-panel' + (state.refineOpen ? ' is-open' : '') + '" id="ft-refine">' +
              refineContent() +
            '</div>' +
          '</div>' +
        '</div>' +
        navButtons(true) +
      '</section>';
  }
  function refineContent() {
    var sodTiers = DATA.SODIUM_TIERS;
    return [
      '<div class="ft-refine-row">' +
        '<div><div class="ft-refine-label">Sweat rate</div><div class="ft-refine-help">ml per hour. Don\u2019t know? Tap below.</div></div>' +
        '<div>' +
          '<input type="number" inputmode="numeric" class="ft-refine-input" data-bind="sweatRateMlH" placeholder="e.g. 900" />' +
          '<button class="ft-btn-link" data-action="open-perf-lab" style="margin-left:12px; margin-top:10px;">I don\u2019t know \u2192</button>' +
        '</div>' +
      '</div>',
      '<div class="ft-refine-row">' +
        '<div><div class="ft-refine-label">Sweat sodium</div><div class="ft-refine-help">Salt residue on skin / clothes after training.</div></div>' +
        '<div class="ft-options ft-options-4" style="gap:8px">' +
          Object.keys(sodTiers).map(function (k) {
            return '<button class="ft-opt" style="min-height:60px;padding:10px" data-q="sodiumTier" data-val="' + k + '"><div class="ft-opt-label" style="font-size:13px">' + sodTiers[k].label + '</div></button>';
          }).join('') +
        '</div>' +
      '</div>',
      '<div class="ft-refine-row">' +
        '<div><div class="ft-refine-label">FTP / Threshold pace</div><div class="ft-refine-help">Cyclists: FTP in watts. Runners: threshold pace in min/km.</div></div>' +
        '<div class="ft-refine-pair">' +
          '<div><label class="ft-eyebrow">FTP (W)</label><br><input type="number" inputmode="numeric" class="ft-refine-input ft-refine-input--narrow" data-bind="ftpW" placeholder="250" /></div>' +
          '<div><label class="ft-eyebrow">Pace (min/km)</label><br><input type="text" class="ft-refine-input ft-refine-input--narrow" data-bind="thresholdPaceMinKm" placeholder="4:15" /></div>' +
        '</div>' +
      '</div>',
      '<div class="ft-refine-row">' +
        '<div><div class="ft-refine-label">Goal</div></div>' +
        '<div class="ft-options ft-options-4" style="gap:8px">' +
          [
            { k:'finish', l:'Finish',              tip:'Just complete the event safely. Conservative carb targets, generous hydration.' },
            { k:'pb',     l:'PB',                  tip:'Beat your best time. Aggressive but defensible carb intake at gut tier ceiling.' },
            { k:'podium', l:'Podium',              tip:'All-out race execution. Top-of-tier carbs and full caffeine load.' },
            { k:'adapt',  l:'Training adaptation', tip:'Build metabolic flexibility. Lower carb intake to stress fat-burning systems.' }
          ].map(function (g) {
            return '<button class="ft-opt" style="min-height:60px;padding:10px" data-q="goal" data-val="' + g.k + '">' +
              '<div class="ft-opt-label" style="font-size:13px;display:flex;align-items:center;gap:6px">' +
                g.l +
                '<span class="ft-info" tabindex="0" role="button" aria-label="' + g.tip.replace(/"/g, '&quot;') + '" data-tip="' + g.tip.replace(/"/g, '&quot;') + '">i</span>' +
              '</div>' +
            '</button>';
          }).join('') +
        '</div>' +
      '</div>',
      '<div class="ft-refine-row">' +
        '<div><div class="ft-refine-label">Dietary</div></div>' +
        '<div class="ft-options ft-options-3" style="gap:8px">' +
          ['standard:Standard', 'vegan:Vegan'].map(function (s) {
            var p = s.split(':');
            return '<button class="ft-opt" style="min-height:60px;padding:10px" data-q="dietary" data-val="' + p[0] + '"><div class="ft-opt-label" style="font-size:13px">' + p[1] + '</div></button>';
          }).join('') +
        '</div>' +
      '</div>'
    ].join('');
  }

  function renderGate() {
    return '' +
      '<section class="ft-screen is-active" data-screen="gate">' +
        '<div class="ft-gate">' +
          '<div class="ft-eyebrow">Plan ready</div>' +
          '<h2 class="ft-h1" style="margin-top:12px">Your fueling plan is ready.</h2>' +
          '<p class="ft-q-help" style="margin:16px auto 0">Drop your name and email to unlock the full timeline, the share-card PNG, and your bundle.</p>' +
          '<div class="ft-gate-card">' +
            '<div class="ft-gate-snippet" aria-hidden="true">' +
              '<div class="ft-gate-tile">' +
                '<div class="ft-gate-tile-value"><span class="ft-gate-stat">90</span></div>' +
                '<div class="ft-gate-tile-label">g/h carbs</div>' +
              '</div>' +
              '<div class="ft-gate-tile">' +
                '<div class="ft-gate-tile-value"><span class="ft-gate-stat">750</span></div>' +
                '<div class="ft-gate-tile-label">ml/h fluid</div>' +
              '</div>' +
              '<div class="ft-gate-tile">' +
                '<div class="ft-gate-tile-value"><span class="ft-gate-stat">450</span></div>' +
                '<div class="ft-gate-tile-label">mg/h sodium</div>' +
              '</div>' +
            '</div>' +
            '<div class="ft-form-row">' +
              '<label for="ft-first-name">First name</label>' +
              '<input id="ft-first-name" type="text" data-bind="firstName" placeholder="Jake" autocomplete="given-name" />' +
            '</div>' +
            '<div class="ft-form-row">' +
              '<label for="ft-email">Email</label>' +
              '<input id="ft-email" type="email" data-bind="email" placeholder="you@example.com" autocomplete="email" aria-describedby="ft-email-error" />' +
              '<span id="ft-email-error" class="ft-form-error" role="alert" aria-live="polite"></span>' +
            '</div>' +
            '<label class="ft-checkbox"><input type="checkbox" data-bind="consent" /> Send me race-week tips and product updates</label>' +
            '<button class="ft-btn ft-btn-primary" id="ft-reveal" style="width:100%;justify-content:center;padding:16px">Reveal my plan \u2192</button>' +
            '<p class="ft-fineprint">No spam. Unsubscribe anytime. UK GDPR compliant.</p>' +
          '</div>' +
        '</div>' +
      '</section>';
  }

  function renderResult() {
    var p = state.plan;
    if (!p) {
      return '' +
        '<section class="ft-screen is-active" data-screen="result">' +
          '<div class="ft-error-state" role="alert">' +
            '<div class="ft-eyebrow">SOMETHING BROKE</div>' +
            '<h2 class="ft-h1" style="margin-top:12px">We couldn’t build your plan.</h2>' +
            '<p class="ft-q-help">An error fired while generating your fueling plan. Your inputs are safe — try again, or go back and adjust.</p>' +
            '<div class="ft-error-actions">' +
              '<button class="ft-btn ft-btn-primary" data-action="retry-plan">Try again →</button>' +
              '<button class="ft-btn ft-btn-ghost" data-action="back-to-gut">← Adjust inputs</button>' +
            '</div>' +
          '</div>' +
        '</section>';
    }

    var summary = (p.input.durationMin / 60).toFixed(p.input.durationMin % 60 === 0 ? 0 : 1) + 'h ' +
                   (DATA.DISCIPLINES[p.input.sport] || {}).label + ' \u00b7 ' +
                   (DATA.INTENSITY[p.input.intensity] || {}).label + ' \u00b7 ' +
                   p.input.weightKg + 'kg \u00b7 ' +
                   (p.input.caffeine === 'half' ? 'half-caf' : p.input.caffeine === 'yes' ? 'full-caf' : 'no caf');

    return '' +
      '<section class="ft-screen is-active" data-screen="result" aria-labelledby="ft-result-heading">' +
        '<h1 id="ft-result-heading" class="ft-sr-only">Your STYRKR fueling plan</h1>' +
        '<div class="ft-result">' +
          /* Block A — targets */
          '<div class="ft-block ft-block-A">' +
            '<div style="display:flex;justify-content:space-between;align-items:baseline;flex-wrap:wrap;gap:12px">' +
              '<div class="ft-eyebrow">Plan ID \u00b7 ' + p.planId + '</div>' +
              '<div class="ft-eyebrow ft-mono">' + new Date().toLocaleDateString('en-GB') + '</div>' +
            '</div>' +
            '<h2 class="ft-sr-only">Targets</h2>' +
            '<div class="ft-targets">' +
              targetCell(p.targets.carbsGH, 'g/h', 'carbs', '[1] [2]') +
              targetCell(p.targets.fluidMlH, 'ml/h', 'fluid', '[4]') +
              targetCell(p.targets.sodiumMgH, 'mg/h', 'sodium', '[5] [10]') +
            '</div>' +
            '<div class="ft-summary-line">For your ' + summary + '</div>' +
          '</div>' +

          /* Block B — timeline */
          '<div class="ft-block ft-block-B">' +
            '<div class="ft-eyebrow">02 \u00b7 Race-day timeline</div>' +
            '<h3 class="ft-h2" style="margin-top:8px">' + p.events.length + ' fueling moments across ' + (p.input.durationMin / 60).toFixed(1) + ' hours.</h3>' +
            renderTimeline(p) +
            '<div class="ft-timeline-actions">' +
              '<button class="ft-btn ft-btn-ghost" data-png="card">\u2193 Race-day card (PNG)</button>' +
              '<button class="ft-btn ft-btn-ghost" data-png="story">\u2193 Instagram story (PNG)</button>' +
              '<button class="ft-btn ft-btn-ghost" id="ft-pdf-btn">\u2193 Print / save as PDF</button>' +
            '</div>' +
          '</div>' +

          /* Block C — bundle */
          '<div class="ft-block ft-block-light ft-block-C">' +
            '<div class="ft-eyebrow">03 \u00b7 Your box</div>' +
            '<h3 class="ft-h2" style="margin-top:8px">Full retail packs, ready to ship.</h3>' +
            '<div class="ft-bundle-list">' +
              p.bundle.map(function (b, idx) {
                return '<div class="ft-bundle-item">' +
                  '<div class="ft-bundle-num">0' + (idx + 1) + '</div>' +
                  '<div>' +
                    '<div class="ft-bundle-title">' + b.product.title + '</div>' +
                    '<div class="ft-bundle-meta">' + b.product.packLabel + ' \u00b7 covers ~' + b.sessionsCovered + ' sessions</div>' +
                  '</div>' +
                  '<div class="ft-bundle-price">' + (b.boxes > 1 ? b.boxes + '\u00d7 ' : '') + money(b.lineTotal) + '</div>' +
                '</div>';
              }).join('') +
            '</div>' +
            '<div class="ft-bundle-total">' +
              '<span class="ft-bundle-total-label">Subtotal</span>' +
              '<span class="ft-bundle-total-num">' + money(p.bundleTotal) + '</span>' +
            '</div>' +
            '<button class="ft-btn ft-btn-primary ft-bundle-cta" id="ft-cart-btn">Add box to cart \u2192</button>' +
            '<span id="ft-cart-status" class="ft-form-error" role="status" aria-live="polite"></span>' +
          '</div>' +

          /* Block D — reasoning */
          '<div class="ft-block ft-block-D">' +
            '<div class="ft-eyebrow">04 \u00b7 Why this plan</div>' +
            '<h3 class="ft-h2" style="margin-top:8px">The science behind every number.</h3>' +
            '<div class="ft-reasons">' +
              p.reasoning.map(function (r) {
                return '<div class="ft-reason">' +
                  '<div class="ft-reason-num">0' + r.n + '</div>' +
                  '<div class="ft-reason-claim">' + r.claim +
                    '<span class="ft-reason-cite">' + r.cites.map(function (c) { return '[' + c + ']'; }).join(' ') + '</span>' +
                  '</div>' +
                '</div>';
              }).join('') +
            '</div>' +
          '</div>' +

          /* Charts block \u2014 primary always visible, secondary collapsed under expander */
          '<div class="ft-block ft-block-charts">' +
            '<div class="ft-eyebrow">05 \u00b7 The numbers in motion</div>' +
            '<h3 class="ft-h2" style="margin-top:8px">Carb balance + hydration.</h3>' +
            '<p class="ft-mute" style="margin-top:8px;font-size:13px;max-width:60ch">Two headline curves up top. Open the expander for the full physiology stack \u2014 glycogen, caffeine, bonk risk, W\u2032 balance, fluid + sodium balance.</p>' +
            '<div class="ft-charts-grid ft-charts-grid--primary">' +
              chartCard('cumCarbs', 'Carb balance', 'intake vs oxidised \u00b7 markers show each fueling moment',
                'Carb balance chart: ' + p.totals.carbsG + ' grams total intake versus oxidised carbohydrate over ' + (p.input.durationMin / 60).toFixed(1) + ' hours.',
                carbBalanceKey()) +
              chartCard('hydration', 'Hydration status', '% body mass loss \u00b7 2% line = performance, 4% = health risk',
                'Hydration loss expressed as percent of starting body mass. Sweat plus respiratory loss minus fluid intake. ACSM 2007 thresholds shown.') +
            '</div>' +
            '<details class="ft-charts-more" id="ft-charts-more">' +
              '<summary class="ft-charts-more-summary">' +
                '<span class="ft-charts-more-label">See full physiology</span>' +
                '<span class="ft-charts-more-sub">glycogen \u00b7 caffeine \u00b7 bonk risk' + (p.meta && p.meta.isCyclistish ? ' \u00b7 W\u2032 balance' : '') + ' \u00b7 fluid \u00b7 sodium</span>' +
                '<span class="ft-charts-more-icon" aria-hidden="true"></span>' +
              '</summary>' +
              '<div class="ft-charts-grid ft-charts-grid--secondary">' +
                chartCard('hourlyCarbs', 'Carbs per hour', 'each bar vs your ' + p.targets.carbsGH + ' g/h target',
                  'Carbs-per-hour bar chart targeting ' + p.targets.carbsGH + ' grams per hour, capped at the ' + (p.targets.gutCeilingGH || p.targets.carbsGH) + ' g/h gut tier ceiling.') +
                chartCard('bonkRisk', 'Bonk risk index', 'composite score 0\u2013100 \u00b7 above 70 is the bonk zone',
                  'Composite bonk risk score from 0 to 100. Forty percent glycogen depletion, thirty percent carb intake gap, twenty percent intensity, ten percent hydration debt.') +
                chartCard('glycogen', 'Glycogen reserve', 'estimated muscle store remaining \u00b7 lower = bonk risk',
                  'Glycogen reserve curve from 100 percent at start, declining as the session burns muscle glycogen offset by external carb intake.') +
                (p.meta && p.meta.isCyclistish ? chartCard('wbal', 'W\u2032 balance', 'anaerobic work reserve \u00b7 starts at ~' + (p.meta.wbalInitialKJ || 20) + ' kJ',
                  'W prime balance, the anaerobic work reserve from Skiba 2012. Depletes above critical power. Adequate fueling reduces drain by up to fifty percent.') : '') +
                chartCard('caffeine', 'Caffeine plasma', 'rising + decaying mg in bloodstream \u00b7 ISSN performance band',
                  'Caffeine plasma curve modelling rises at each caffeine event and decay at a 5 hour half life. Total plan dose ' + p.totals.caffeineMg + ' milligrams.') +
                chartCard('fluid', 'Fluid balance', 'intake vs estimated sweat loss',
                  'Fluid balance: ' + p.totals.fluidMl + ' millilitres planned intake versus estimated sweat loss at ' + p.targets.sweatRate + ' millilitres per hour.') +
                chartCard('sodium', 'Sodium balance', 'intake vs estimated sweat loss',
                  'Sodium balance: ' + p.totals.sodiumMg + ' milligrams planned intake versus estimated sweat loss.') +
              '</div>' +
            '</details>' +
          '</div>' +

          /* Credibility strip */
          '<div class="ft-block-cred ft-cred">' +
            '<div class="ft-cred-cell">' +
              '<div class="ft-eyebrow" style="margin-bottom:8px">Built on the science</div>' +
              'Plans grounded in published research from <strong>Jeukendrup, Burke, Baker, Maughan, Costa</strong> and the IOC consensus statements.' +
              '<button class="ft-method-toggle" data-action="open-citations" style="margin-top:16px">Methodology + ' + Object.keys(DATA.CITATIONS).length + ' citations</button>' +
            '</div>' +
            '<div class="ft-cred-cell"><div class="ft-eyebrow" style="margin-bottom:8px">Want exact numbers?</div><strong>STYRKR Performance Lab</strong> offers in-person sweat-sodium testing for athletes who want lab-precise plans. <a href="#" data-action="open-perf-lab" style="text-decoration:underline;color:var(--ft-accent)">Find out more \u2192</a></div>' +
            '<div class="ft-cred-cell"><div class="ft-eyebrow" style="margin-bottom:8px">UK-made fuel</div>STYRKR products are formulated and produced in the UK, using a 1:0.8 maltodextrin\u2013fructose ratio for high-rate dual-carb absorption.</div>' +
          '</div>' +
        '</div>' +

        /* Sticky mobile CTA */
        '<div class="ft-cta-sticky">' +
          '<div class="ft-cta-sticky-status"><span class="ft-cta-sticky-dot"></span>Plan ready \u00b7 ' + money(p.bundleTotal) + '</div>' +
          '<button class="ft-btn ft-btn-primary" data-action="cart" style="padding:10px 18px">Add box \u2192</button>' +
        '</div>' +
      '</section>';
  }

  function targetCell(num, unit, label, cite) {
    return '<div class="ft-target">' +
      '<div class="ft-target-num" data-counter="' + num + '">0</div>' +
      '<div class="ft-target-unit">' + unit + ' \u00b7 ' + label + '</div>' +
      '<div class="ft-target-tag">' + cite + '</div>' +
    '</div>';
  }

  function chartCard(id, title, sub, srSummary, footer) {
    var summary = srSummary || (title + ' — ' + sub);
    return '<figure class="ft-chart-card" role="figure" aria-label="' + summary.replace(/"/g, '&quot;') + '">' +
      '<figcaption class="ft-chart-cap">' +
        '<div class="ft-chart-title">' + title + '</div>' +
        '<div class="ft-chart-sub">' + sub + '</div>' +
      '</figcaption>' +
      '<div class="ft-chart" id="ft-chart-' + id + '" aria-hidden="true"></div>' +
      (footer || '') +
    '</figure>';
  }
  // Product-type key — visual legend matching the carb-balance chart markers.
  function carbBalanceKey() {
    return '<div class="ft-chart-key" aria-hidden="true">' +
      '<span class="ft-key-item"><span class="ft-key-dot ft-key-dot--mix"></span>MIX (drink)</span>' +
      '<span class="ft-key-item"><span class="ft-key-dot ft-key-dot--gel"></span>GEL</span>' +
      '<span class="ft-key-item"><span class="ft-key-dot ft-key-dot--bar"></span>BAR</span>' +
      '<span class="ft-key-item"><span class="ft-key-dot ft-key-dot--slt"></span>SLT+ (electrolyte)</span>' +
    '</div>';
  }

  function renderTimeline(p) {
    var dur = p.input.durationMin;
    var preStart = (p.targets && p.targets.preStartMin) || 0;
    var range = dur - preStart;
    function pctOf(timeMin) { return ((timeMin - preStart) / range) * 100; }
    function fmt(timeMin) {
      var neg = timeMin < 0;
      var abs = Math.abs(timeMin);
      var hh = Math.floor(abs / 60), mm = abs % 60;
      return (neg ? '\u2212' : '') + hh + ':' + (mm < 10 ? '0' + mm : mm);
    }
    var ticks = [];
    var tickInterval = dur > 480 ? 120 : dur > 240 ? 60 : 30;
    // Pre-race tick (only if there's a pre-race window).
    // We render only the START divider here — the pre-race time is conveyed by the
    // event's own "PRE" tag. Avoids the duplicate "-0:30" overlap.
    if (preStart < 0) {
      ticks.push('<div class="ft-timeline-tick is-pre" style="left:' + pctOf(preStart) + '%"></div>');
      ticks.push('<div class="ft-timeline-divider" style="left:' + pctOf(0) + '%" aria-label="race start"></div>' +
                 '<div class="ft-timeline-tick-label is-divider" style="left:' + pctOf(0) + '%">START</div>');
    }
    // If we already drew a START divider at t=0, skip the redundant "0:00" tick.
    var firstTick = (preStart < 0) ? tickInterval : 0;
    for (var t = firstTick; t <= dur; t += tickInterval) {
      ticks.push('<div class="ft-timeline-tick" style="left:' + pctOf(t) + '%"></div>' +
                 '<div class="ft-timeline-tick-label" style="left:' + pctOf(t) + '%">' + fmt(t) + '</div>');
    }

    // Adaptive label density:
    //   < 8 events: show all labels
    //   8–14 events: show every 2nd label, others reveal on hover
    //   15+ events: show only event dots, all labels reveal on hover
    var density = p.events.length < 8 ? 'all' : p.events.length < 15 ? 'half' : 'sparse';

    // ── CLUSTER STAGGER ──
    // When events fall within ~10% of the timeline of each other, their labels overlap.
    // Walk through in order and assign each visible-label event a row index — the row resets
    // once a gap of CLUSTER_GAP_PCT clears. Renders as vertically stacked labels per cluster.
    var CLUSTER_GAP_PCT = 11;       // anything closer counts as the same cluster
    var rowsActive = [];            // [{ pct, row }] — most recent event per row
    var rowAssign = new Array(p.events.length);
    p.events.forEach(function (ev, idx) {
      var pct = pctOf(ev.timeMin);
      var isPre = ev.timeMin < 0;
      var labelVisible =
        density === 'all'   ? true :
        density === 'half'  ? (idx % 2 === 0 || isPre) :
                              isPre;
      if (!labelVisible) { rowAssign[idx] = 0; return; }
      // Find lowest available row whose last event is far enough away
      var assigned = -1;
      for (var r = 0; r < 4; r++) {
        var last = rowsActive[r];
        if (!last || (pct - last) >= CLUSTER_GAP_PCT) { assigned = r; break; }
      }
      if (assigned === -1) assigned = 0;        // saturate at row 0 if more than 4 clustered
      rowsActive[assigned] = pct;
      rowAssign[idx] = assigned;
    });

    var events = p.events.map(function (ev, idx) {
      var pct = pctOf(ev.timeMin);
      var isCaf = ev.product.caffeinated;
      var isPre = ev.timeMin < 0;
      var labelVisible =
        density === 'all'   ? true :
        density === 'half'  ? (idx % 2 === 0 || isPre) :
                              isPre;
      var timeStr = fmt(ev.timeMin);
      var tooltip = ev.product.title + ' · +' + ev.carbs + 'g carbs at ' + timeStr;
      // Edge-aware tooltip flagging — prevents overflow at left/right ends of the axis.
      var nearStart = pct < 12;
      var nearEnd = pct > 88;
      var row = rowAssign[idx] || 0;
      return '<div class="ft-timeline-event' +
        (isCaf ? ' is-caf' : '') +
        (isPre ? ' is-pre' : '') +
        (labelVisible ? '' : ' is-quiet') +
        (nearStart ? ' is-near-start' : '') +
        (nearEnd ? ' is-near-end' : '') + '"' +
        ' style="left:' + pct + '%"' +
        ' data-row="' + row + '"' +
        ' data-tip="' + tooltip.replace(/"/g, '&quot;') + '"' +
        ' tabindex="0" role="button" aria-label="' + tooltip + '">' +
        '<div class="ft-timeline-event-dot"></div>' +
        '<div class="ft-timeline-event-pop">' +
          '<div class="ft-timeline-event-product">' + ev.product.shortTitle + '</div>' +
          '<div class="ft-timeline-event-label">' + timeStr + ' · +' + ev.carbs + 'g</div>' +
        '</div>' +
      '</div>';
    });

    // Mobile: enforce minimum width per event so timeline is scrollable, not squished
    var minWidthPx = Math.max(720, p.events.length * 90);
    return '<div class="ft-timeline-scroll">' +
      '<div class="ft-timeline-wrap" style="min-width:' + minWidthPx + 'px">' +
        '<div class="ft-timeline-axis">' +
          ticks.join('') +
          events.join('') +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="ft-timeline-hint">Swipe to scan timeline</div>' +
    '<div class="ft-timeline-legend ft-mute">' +
      (density === 'all' ? 'All ' + p.events.length + ' events labelled.' :
       density === 'half' ? 'Hover any dot for full product name. ' + p.events.length + ' events total.' :
       'Many events \u2014 hover any dot to see the product. ' + p.events.length + ' events total.') +
    '</div>';
  }

  function navButtons(showSubmit) {
    return '<div class="ft-nav">' +
      '<button class="ft-btn ft-btn-ghost" data-action="back">\u2190 Back</button>' +
      '<button class="ft-btn ft-btn-primary" data-action="next" id="ft-next-btn">' + (showSubmit ? 'Build my plan \u2192' : 'Continue \u2192') + '</button>' +
    '</div>';
  }

  /* ─────────────────────────────────────────────
     ROUTING
  ───────────────────────────────────────────── */
  function nextScreen() {
    var idx = SCREENS.indexOf(state.currentScreen);
    state.currentScreen = SCREENS[idx + 1] || state.currentScreen;

    if (state.currentScreen === 'gate' && !state.plan) {
      try {
        state.plan = DATA.generatePlan(buildPlanInput());
      } catch (err) {
        console.error('[fuel-tool] plan generation failed:', err);
        state.plan = null;
      }
    }
    render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function prevScreen() {
    var idx = SCREENS.indexOf(state.currentScreen);
    state.currentScreen = SCREENS[Math.max(0, idx - 1)] || 'welcome';
    render();
  }

  function buildPlanInput() {
    var weightKg = state.weightUnit === 'lb' ? Math.round(state.weightKg * 0.453592) : state.weightKg;
    return {
      mode: state.mode,
      sessionType: state.sessionType,
      sport: state.sport,
      durationMin: state.durationMin,
      intensity: state.intensity || 'steady',
      weightKg: weightKg,
      caffeine: state.caffeine || 'half',
      weighting: state.weighting || 'balanced',
      gutTier: state.gutTier || 'experienced',
      sweatRateMlH: state.sweatRateMlH ? +state.sweatRateMlH : null,
      sodiumTier: state.sodiumTier,
      ftpW: state.ftpW ? +state.ftpW : null,
      thresholdPaceMinKm: state.thresholdPaceMinKm,
      goal: state.goal || 'pb',
      dietary: state.dietary,
      flavour: state.flavour
    };
  }

  function canAdvance() {
    switch (state.currentScreen) {
      case 'welcome':   return !!state.mode;
      case 'sport':     return !!state.sport;
      case 'session':   return !!state.sessionType;
      case 'duration':  return state.durationMin > 0;
      case 'intensity': return !!state.intensity;
      case 'weight':    return state.weightKg >= 30 && state.weightKg <= 200;
      case 'caffeine':  return !!state.caffeine;
      case 'weighting': return !!state.weighting;
      case 'gut':       return !!state.gutTier;
      default:          return true;
    }
  }

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  function render() {
    var root = el('ft-stage');
    if (!root) return;

    var screen = state.currentScreen;
    var html = '';
    if (screen === 'welcome')   html = renderWelcome();
    else if (screen === 'sport')     html = renderSport();
    else if (screen === 'session')   html = renderSession();
    else if (screen === 'duration')  html = renderDuration();
    else if (screen === 'intensity') html = renderIntensity();
    else if (screen === 'weight')    html = renderWeight();
    else if (screen === 'caffeine')  html = renderCaffeine();
    else if (screen === 'weighting') html = renderWeighting();
    else if (screen === 'gut')       html = renderGut();
    else if (screen === 'gate')      html = renderGate();
    else if (screen === 'result')    html = renderResult();

    root.innerHTML = html;
    updateProgress();
    updateSelections();
    bindEvents();
    // Save after every screen transition. Covers nav (next/prev) and any
    // re-render triggered by state changes — a single save site keeps things
    // simple and guarantees we never miss an update.
    persistState();

    if (screen === 'result') {
      animateCounters();
      // Render the 2 primary charts immediately. Secondary charts lazy-mount the first
      // time the user opens the "See full physiology" expander.
      var charts = window.STYRKR_FUEL_CHARTS;
      if (charts) {
        (charts.renderPrimary || charts.render)(state.plan);
      }
      var moreEl = el('ft-charts-more');
      if (moreEl && charts && charts.renderSecondary) {
        var rendered = false;
        moreEl.addEventListener('toggle', function () {
          if (!moreEl.open || rendered) return;
          rendered = true;
          charts.renderSecondary(state.plan);
        });
      }
    }

    // Disable next if not ready
    var nextBtn = el('ft-next-btn');
    if (nextBtn) nextBtn.disabled = !canAdvance();
  }

  function updateProgress() {
    var bar = el('ft-progress-bar');
    var stepIndicator = el('ft-step-indicator');
    var idx = SCREENS.indexOf(state.currentScreen);
    var pct = state.currentScreen === 'welcome' ? 0
           : state.currentScreen === 'result' ? 100
           : Math.round((idx / (SCREENS.length - 2)) * 100);
    if (bar) bar.style.width = pct + '%';
    if (stepIndicator) {
      if (state.currentScreen === 'welcome') {
        stepIndicator.style.display = 'none';
      } else {
        stepIndicator.style.display = '';
        var coreIdx = ['sport', 'session', 'duration', 'intensity', 'weight', 'caffeine', 'weighting', 'gut'].indexOf(state.currentScreen);
        var n = coreIdx >= 0 ? (coreIdx + 1) : (state.currentScreen === 'gate' ? 8 : state.currentScreen === 'result' ? 9 : 0);
        stepIndicator.innerHTML = '<span class="ft-step-current">' + (n < 10 ? '0' + n : n) + '</span> / 09';
      }
    }
  }

  function updateSelections() {
    qsa('.ft-opt[data-q]').forEach(function (b) {
      var q = b.getAttribute('data-q');
      var v = b.getAttribute('data-val');
      if (state[q] === v) b.classList.add('is-selected');
      else b.classList.remove('is-selected');
    });
  }

  function animateCounters() {
    qsa('[data-counter]').forEach(function (el) {
      var target = +el.getAttribute('data-counter');
      var start = 0;
      var dur = 1100;
      var t0 = performance.now();
      function step(now) {
        var p = Math.min(1, (now - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(start + (target - start) * eased);
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    });
  }

  /* ─────────────────────────────────────────────
     EVENT BINDING
  ───────────────────────────────────────────── */
  function bindEvents() {
    qsa('.ft-mode-card').forEach(function (b) {
      b.addEventListener('click', function () {
        state.mode = b.getAttribute('data-mode');
        trackKlaviyo('Started Fuel Tool', { mode: state.mode });
        nextScreen();
      });
    });

    qsa('.ft-opt[data-q]').forEach(function (b) {
      b.addEventListener('click', function () {
        var q = b.getAttribute('data-q');
        var v = b.getAttribute('data-val');
        state[q] = v;
        updateSelections();
        var nextBtn = el('ft-next-btn');
        if (nextBtn) nextBtn.disabled = !canAdvance();
      });
    });

    var dur = el('ft-dur-slider');
    if (dur) {
      dur.addEventListener('input', function () {
        state.durationMin = +dur.value;
        var hrs = Math.floor(state.durationMin / 60);
        var mins = state.durationMin % 60;
        el('ft-dur-readout').innerHTML = hrs + '<span style="font-size:0.5em;color:var(--ft-mute);font-weight:400">h</span> ' + (mins < 10 ? '0' + mins : mins) + '<span style="font-size:0.5em;color:var(--ft-mute);font-weight:400">m</span>';
        var min = +dur.min, max = +dur.max;
        dur.style.setProperty('--ft-fill', (((state.durationMin - min) / (max - min)) * 100) + '%');
      });
    }

    var weight = el('ft-weight-input');
    if (weight) {
      weight.addEventListener('input', function () {
        state.weightKg = +weight.value;
        var nextBtn = el('ft-next-btn');
        if (nextBtn) nextBtn.disabled = !canAdvance();
      });
    }
    // +/- steppers — adjust by ±1, clamp to min/max, fire input event so listeners update
    qsa('[data-step]').forEach(function (b) {
      b.addEventListener('click', function () {
        var input = el('ft-weight-input');
        if (!input) return;
        var step = parseInt(b.getAttribute('data-step'), 10) || 0;
        var min = +(input.getAttribute('min') || 30);
        var max = +(input.getAttribute('max') || 200);
        var next = Math.max(min, Math.min(max, (+input.value || 0) + step));
        input.value = next;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
    qsa('[data-unit]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.weightUnit = b.getAttribute('data-unit');
        qsa('[data-unit]').forEach(function (x) {
          x.classList.remove('is-active');
          x.setAttribute('aria-pressed', 'false');
        });
        b.classList.add('is-active');
        b.setAttribute('aria-pressed', 'true');
      });
    });

    qsa('[data-bind]').forEach(function (input) {
      var key = input.getAttribute('data-bind');
      input.addEventListener('input', function () {
        state[key] = input.type === 'checkbox' ? input.checked : input.value;
      });
    });

    qsa('[data-action="next"]').forEach(function (b) {
      b.addEventListener('click', function () { if (canAdvance()) nextScreen(); });
    });
    qsa('[data-action="back"]').forEach(function (b) {
      b.addEventListener('click', prevScreen);
    });
    qsa('[data-action="toggle-refine"]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.refineOpen = !state.refineOpen;
        var panel = el('ft-refine');
        if (panel) panel.classList.toggle('is-open');
      });
    });
    qsa('[data-action="open-citations"]').forEach(function (b) {
      b.addEventListener('click', function () {
        var m = el('ft-modal-citations');
        if (m) m.classList.add('is-open');
      });
    });
    qsa('[data-action="open-perf-lab"]').forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var m = el('ft-modal-perf-lab');
        if (m) m.classList.add('is-open');
      });
    });
    qsa('[data-action="close-modal"]').forEach(function (b) {
      b.addEventListener('click', function () {
        qsa('.ft-modal').forEach(function (m) { m.classList.remove('is-open'); });
      });
    });
    // Retry / adjust buttons on the plan-failure error state
    qsa('[data-action="retry-plan"]').forEach(function (b) {
      b.addEventListener('click', function () {
        try {
          state.plan = DATA.generatePlan(buildPlanInput());
        } catch (err) { state.plan = null; }
        render();
      });
    });
    qsa('[data-action="back-to-gut"]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.currentScreen = 'gut';
        state.plan = null;
        render();
      });
    });

    var revealBtn = el('ft-reveal');
    if (revealBtn) {
      revealBtn.addEventListener('click', function () {
        var emailInput = el('ft-email');
        var emailError = el('ft-email-error');
        var valid = state.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email);
        if (!valid) {
          if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
          if (emailError) emailError.textContent = 'Enter a valid email so we can send your plan.';
          if (emailInput) emailInput.focus();
          return;
        }
        if (emailInput) emailInput.removeAttribute('aria-invalid');
        if (emailError) emailError.textContent = '';
        var input = buildPlanInput();
        identifyKlaviyo({
          $email: state.email,
          $first_name: state.firstName,
          fuel_tool_sport: input.sport,
          fuel_tool_gut_tier: input.gutTier,
          fuel_tool_caffeine: input.caffeine,
          fuel_tool_weighting: input.weighting
        });
        trackKlaviyo('Plan Generated', {
          plan_id: state.plan.planId,
          sport: input.sport,
          duration_min: input.durationMin,
          carb_target_gh: state.plan.targets.carbsGH,
          bundle_total: state.plan.bundleTotal
        });
        state.currentScreen = 'result';
        render();
      });
    }

    var cartBtn = el('ft-cart-btn');
    if (cartBtn) {
      cartBtn.addEventListener('click', addBundleToCart);
    }
    qsa('[data-action="cart"]').forEach(function (b) {
      b.addEventListener('click', addBundleToCart);
    });

    qsa('[data-png]').forEach(function (b) {
      b.addEventListener('click', function () {
        downloadShareCard(b.getAttribute('data-png'));
      });
    });
    var pdfBtn = el('ft-pdf-btn');
    if (pdfBtn) pdfBtn.addEventListener('click', function () { window.print(); });
  }

  /* ─────────────────────────────────────────────
     CART INTEGRATION
  ───────────────────────────────────────────── */
  function addBundleToCart() {
    var p = state.plan;
    if (!p) return;
    var items = p.bundle.map(function (b) {
      return { id: b.product.variantId, quantity: b.boxes };
    });
    var cartBtn = el('ft-cart-btn');
    var originalLabel = cartBtn ? cartBtn.textContent : '';
    if (cartBtn) {
      cartBtn.disabled = true;
      cartBtn.setAttribute('aria-busy', 'true');
      cartBtn.textContent = 'Adding to cart\u2026';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart add failed');
      return r.json();
    }).then(function () {
      trackKlaviyo('Plan Added to Cart', {
        plan_id: p.planId,
        bundle_total: p.bundleTotal,
        skus: p.bundle.map(function (b) { return b.product.shortTitle; }).join(', ')
      });
      window.location.href = '/cart';
    }).catch(function (err) {
      if (cartBtn) {
        cartBtn.disabled = false;
        cartBtn.removeAttribute('aria-busy');
        cartBtn.textContent = originalLabel || 'Add box to cart \u2192';
      }
      var status = el('ft-cart-status');
      if (status) status.textContent = 'Couldn\u2019t add to cart \u2014 please try again.';
      console.error('[fuel-tool] cart add failed:', err);
    });
  }

  /* ─────────────────────────────────────────────
     PNG EXPORTS — two formats, both white-on-black-and-lime
       1. card  → A4-portrait race-day reference (1080x1920)
       2. story → Instagram story (1080x1920, brand-led)
     Uses html2canvas (CDN-loaded on first click).
  ───────────────────────────────────────────── */
  function downloadShareCard(format) {
    format = format || 'card';
    var btn = qs('[data-png="' + format + '"]');
    var btnLabel = btn ? btn.textContent : '';
    if (btn) btn.textContent = 'Generating\u2026';

    function doRender() {
      var node = format === 'story' ? buildStoryHtml() : buildCardHtml();
      document.body.appendChild(node);
      window.html2canvas(node, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      }).then(function (canvas) {
        var link = document.createElement('a');
        link.download = 'styrkr-fuel-' + format + '-' + state.plan.planId + '.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
        document.body.removeChild(node);
        if (btn) btn.textContent = btnLabel;
        trackKlaviyo('Plan PNG Downloaded', { plan_id: state.plan.planId, format: format });
      }).catch(function (err) {
        console.error('[fuel-tool] PNG render failed:', err);
        if (btn) btn.textContent = 'Error \u2014 try again';
        try { document.body.removeChild(node); } catch (e) {}
      });
    }

    if (window.html2canvas) doRender();
    else {
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      s.onload = doRender;
      document.head.appendChild(s);
    }
  }

  /* RACE-DAY CARD — white canvas, black type, lime accents.
     Designed to print A4 / save to phone home screen. */
  function buildCardHtml() {
    var p = state.plan;
    var summary = (p.input.durationMin / 60).toFixed(p.input.durationMin % 60 === 0 ? 0 : 1) + 'h \u00b7 ' +
                  (DATA.DISCIPLINES[p.input.sport] || {}).label + ' \u00b7 ' +
                  (DATA.INTENSITY[p.input.intensity] || {}).label;
    var name = state.firstName || 'ATHLETE';
    var node = document.createElement('div');
    node.style.cssText = "position:fixed;top:-9999px;left:0;width:1080px;height:1528px;background:#ffffff;color:#0a0a0a;font-family:'Helvetica Neue',Helvetica,-apple-system,BlinkMacSystemFont,Arial,sans-serif;padding:96px 80px;box-sizing:border-box;";
    var brandRow =
      '<div style="display:flex;justify-content:space-between;align-items:center;font-family:ui-monospace,SF Mono,Menlo,monospace;font-size:14px;letter-spacing:0.18em;font-weight:700">' +
        '<span>STYRKR \u00b7 FUEL PLAN</span>' +
        '<span style="color:#0a0a0a;font-weight:500;letter-spacing:0.1em">' + p.planId + '</span>' +
      '</div>';
    var heading =
      '<div style="margin-top:48px;font-family:ui-monospace,monospace;font-size:12px;letter-spacing:0.16em;color:rgba(10,10,10,0.55);text-transform:uppercase">For ' + name + '</div>' +
      '<div style="margin-top:8px;font-size:22px;color:#0a0a0a">' + summary + '</div>';
    var stats =
      '<div style="margin-top:64px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;border-top:1px solid rgba(10,10,10,0.15);border-bottom:1px solid rgba(10,10,10,0.15);padding:32px 0">' +
        statBlock(p.targets.carbsGH, 'g/h carbs') +
        statBlock(p.targets.fluidMlH, 'ml/h fluid') +
        statBlock(p.targets.sodiumMgH, 'mg/h sodium') +
      '</div>';
    var timelineLabel =
      '<div style="margin-top:48px;font-family:ui-monospace,monospace;font-size:12px;letter-spacing:0.16em;color:rgba(10,10,10,0.55);text-transform:uppercase">Race-day timeline</div>';
    var timeline = '<div style="margin-top:16px">' +
      p.events.slice(0, 18).map(function (ev) {
        var t = Math.floor(ev.timeMin / 60) + ':' + (ev.timeMin % 60 < 10 ? '0' : '') + (ev.timeMin % 60);
        return '<div style="display:flex;align-items:baseline;justify-content:space-between;padding:14px 0;border-bottom:1px solid rgba(10,10,10,0.08);font-size:18px">' +
          '<span style="font-family:ui-monospace,monospace;color:rgba(10,10,10,0.55);width:80px;letter-spacing:0.04em">' + t + '</span>' +
          '<span style="flex:1;color:#0a0a0a;font-weight:600">' + ev.product.title.replace(/STYRKR\s*\u2014?\s*/i, '') + '</span>' +
          '<span style="color:#0a0a0a;font-family:ui-monospace,monospace;font-weight:600;background:#d4ff3a;padding:4px 10px;border-radius:99px;font-size:14px">+' + ev.carbs + 'g</span>' +
        '</div>';
      }).join('') +
    '</div>';
    var footer =
      '<div style="position:absolute;bottom:64px;left:80px;right:80px;display:flex;justify-content:space-between;font-family:ui-monospace,monospace;font-size:11px;color:rgba(10,10,10,0.5);letter-spacing:0.12em;text-transform:uppercase">' +
        '<span>Built on the science</span>' +
        '<span>styrkr.com/pages/fuel-tool</span>' +
      '</div>';
    node.innerHTML = brandRow + heading + stats + timelineLabel + timeline + footer;
    return node;
  }

  function statBlock(num, unit) {
    return '<div>' +
      '<div style="font-size:84px;font-weight:700;line-height:0.95;letter-spacing:-0.03em;color:#0a0a0a;font-feature-settings:\'tnum\' 1">' + num + '</div>' +
      '<div style="font-family:ui-monospace,monospace;font-size:11px;letter-spacing:0.14em;color:rgba(10,10,10,0.55);text-transform:uppercase;margin-top:8px">' + unit + '</div>' +
    '</div>';
  }

  /* INSTAGRAM STORY — 1080x1920, brand-led, social-shareable.
     White canvas, black wordmark, lime accent block, generous padding,
     subtle outer border for depth. */
  function buildStoryHtml() {
    var p = state.plan;
    var name = (state.firstName || 'I').toUpperCase();
    var sport = (DATA.DISCIPLINES[p.input.sport] || {}).label || '';
    var dur = (p.input.durationMin / 60).toFixed(p.input.durationMin % 60 === 0 ? 0 : 1);
    var node = document.createElement('div');
    node.style.cssText = "position:fixed;top:-9999px;left:0;width:1080px;height:1920px;background:#ffffff;color:#0a0a0a;font-family:'Helvetica Neue',Helvetica,-apple-system,BlinkMacSystemFont,Arial,sans-serif;padding:64px;box-sizing:border-box;";
    node.innerHTML =
      // Outer border frame
      '<div style="height:100%;border:1px solid rgba(10,10,10,0.15);border-radius:24px;padding:80px 72px;display:flex;flex-direction:column;justify-content:space-between;box-sizing:border-box;position:relative">' +
        // Top: wordmark
        '<div>' +
          '<div style="font-family:Helvetica Neue,Helvetica,-apple-system,BlinkMacSystemFont,Arial,sans-serif;font-weight:800;font-size:48px;letter-spacing:-0.02em;color:#0a0a0a">STYRKR</div>' +
          '<div style="margin-top:8px;font-family:ui-monospace,monospace;font-size:14px;letter-spacing:0.18em;color:rgba(10,10,10,0.55);text-transform:uppercase">Fuel Tool</div>' +
        '</div>' +
        // Middle: hero stat
        '<div style="text-align:left">' +
          '<div style="font-family:ui-monospace,monospace;font-size:14px;letter-spacing:0.18em;color:rgba(10,10,10,0.55);text-transform:uppercase">' + name + '\u2019S PLAN</div>' +
          '<div style="margin-top:24px;font-size:240px;font-weight:800;line-height:0.9;letter-spacing:-0.05em;color:#0a0a0a;font-feature-settings:\'tnum\' 1">' +
            p.targets.carbsGH +
            '<span style="font-size:80px;letter-spacing:-0.02em;color:#0a0a0a">g/h</span>' +
          '</div>' +
          '<div style="margin-top:8px;font-size:32px;color:rgba(10,10,10,0.7);font-weight:500;letter-spacing:-0.01em">carbs across ' + dur + ' hours of ' + sport.toLowerCase() + '</div>' +
          // Lime stat block
          '<div style="margin-top:56px;display:inline-block;padding:24px 32px;background:#d4ff3a;border-radius:16px">' +
            '<div style="display:flex;gap:48px">' +
              storyMini(p.targets.fluidMlH, 'ml/h fluid') +
              storyMini(p.targets.sodiumMgH, 'mg/h sodium') +
              storyMini(p.events.length, 'fuel moments') +
            '</div>' +
          '</div>' +
        '</div>' +
        // Bottom: CTA + handle
        '<div>' +
          '<div style="font-size:36px;font-weight:600;color:#0a0a0a;letter-spacing:-0.01em;line-height:1.2;max-width:80%">Built my fueling plan in 3 minutes.</div>' +
          '<div style="margin-top:16px;font-size:24px;color:rgba(10,10,10,0.55)">Build yours \u2192 styrkr.com/pages/fuel-tool</div>' +
          '<div style="margin-top:48px;display:flex;justify-content:space-between;align-items:center;font-family:ui-monospace,monospace;font-size:12px;letter-spacing:0.14em;color:rgba(10,10,10,0.45);text-transform:uppercase;border-top:1px solid rgba(10,10,10,0.12);padding-top:24px">' +
            '<span>Built on the science</span>' +
            '<span>@styrkr</span>' +
          '</div>' +
        '</div>' +
      '</div>';
    return node;
  }

  function storyMini(num, unit) {
    return '<div>' +
      '<div style="font-size:48px;font-weight:700;line-height:0.95;letter-spacing:-0.02em;color:#0a0a0a;font-feature-settings:\'tnum\' 1">' + num + '</div>' +
      '<div style="font-family:ui-monospace,monospace;font-size:11px;letter-spacing:0.12em;color:rgba(10,10,10,0.7);text-transform:uppercase;margin-top:4px">' + unit + '</div>' +
    '</div>';
  }

  /* ─────────────────────────────────────────────
     MOUNT
  ───────────────────────────────────────────── */
  function mount() {
    var app = qs('.ft-app');
    if (!app) return;

    // Inject ApexCharts CDN if not already present
    if (!window.ApexCharts) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/apexcharts@3.45.0/dist/apexcharts.min.js';
      s.onload = function () { /* available for chart render later */ };
      document.head.appendChild(s);
    }

    // Append modal once (Performance Lab)
    var modal = document.createElement('div');
    modal.className = 'ft-modal';
    modal.id = 'ft-modal-perf-lab';
    modal.innerHTML =
      '<div class="ft-modal-card" style="position:relative">' +
        '<button class="ft-modal-close" data-action="close-modal">\u00d7</button>' +
        '<div class="ft-eyebrow">STYRKR Performance Lab</div>' +
        '<h3 class="ft-h2" style="margin-top:8px">Don\u2019t guess your sweat rate \u2014 measure it.</h3>' +
        '<p style="margin-top:16px;color:var(--ft-mute);font-size:14px;line-height:1.55">Get an in-person sweat-sodium and physiology test. We\u2019ll generate an exact, lab-precise plan tuned to your individual physiology.</p>' +
        '<a href="/pages/sigr-human-performance-lab" class="ft-btn ft-btn-primary" style="margin-top:24px">Find out more \u2192</a>' +
      '</div>';
    app.appendChild(modal);

    // Citations modal \u2014 3-column grid of paper cards, opens from "Methodology + N citations" button.
    var citeModal = document.createElement('div');
    citeModal.className = 'ft-modal ft-modal--wide';
    citeModal.id = 'ft-modal-citations';
    var cards = Object.keys(DATA.CITATIONS).map(function (n) {
      var c = DATA.CITATIONS[n];
      var hasUrl = !!c.url;
      var open = hasUrl ? '<a class="ft-cite-card" href="' + c.url + '" target="_blank" rel="noopener noreferrer">'
                        : '<div class="ft-cite-card is-static">';
      var close = hasUrl ? '</a>' : '</div>';
      return open +
        '<div class="ft-cite-card-head">' +
          '<span class="ft-cite-card-num">[' + n + ']</span>' +
          '<span class="ft-cite-card-tag">' + (c.tag || '') + '</span>' +
        '</div>' +
        '<div class="ft-cite-card-title">' + c.title + '</div>' +
        '<div class="ft-cite-card-meta">' +
          '<span class="ft-cite-card-authors">' + c.authors + '</span>' +
          '<span class="ft-cite-card-year">' + c.year + '</span>' +
        '</div>' +
        '<div class="ft-cite-card-journal">' + c.journal + ' \u00b7 ' + c.vol + ' \u00b7 ' + c.pages + '</div>' +
        (hasUrl ? '<div class="ft-cite-card-link">Open paper \u2192</div>' : '') +
      close;
    }).join('');
    citeModal.innerHTML =
      '<div class="ft-modal-card ft-modal-card--wide" style="position:relative">' +
        '<button class="ft-modal-close" data-action="close-modal">\u00d7</button>' +
        '<div class="ft-eyebrow">Methodology + references</div>' +
        '<h3 class="ft-h2" style="margin-top:8px">The science behind every number.</h3>' +
        '<p class="ft-cite-intro">Every numeric output in your plan is grounded in published sport-science research. Click any card to open the paper.</p>' +
        '<div class="ft-cite-grid">' + cards + '</div>' +
      '</div>';
    app.appendChild(citeModal);

    // Bind close on modal background (any modal)
    [modal, citeModal].forEach(function (m) {
      m.addEventListener('click', function (e) {
        if (e.target === m) m.classList.remove('is-open');
      });
    });

    // Restore any saved session BEFORE the first render so the user lands
    // back on their last screen with all inputs intact.
    restoreState();
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

})(window, document);
